import axios from 'axios'

/**
 * Servicio (singleton) para integrar el widget de HubSpot Conversations.
 *
 * El frontend nunca maneja claves de HubSpot: sólo consume los endpoints
 * del backend `/hubspot/config` y `/hubspot/identify`. Toda la integración
 * es "best-effort": si algo falla, la app debe seguir funcionando.
 *
 * Docs:
 *  - Visitor Identification: https://developers.hubspot.com/docs/api/conversation/visitor-identification
 *  - Widget JS API: window.HubSpotConversations (widget.load(), clear(), hsConversationsOnReady)
 */

const HS_SCRIPT_ID = 'hs-script-loader'

const service = {
    config: null,
    loadingConfig: null,
    scriptInjected: false,

    /**
     * Carga (y cachea) la configuración pública del widget.
     * No requiere autenticación.
     */
    loadConfig() {
        if (this.config) {
            return Promise.resolve(this.config)
        }
        if (this.loadingConfig) {
            return this.loadingConfig
        }

        this.loadingConfig = axios.get('/hubspot/config')
            .then(({ data }) => {
                this.config = data
                return data
            })
            .catch((error) => {
                // El chat es best-effort: no debe romper la app.
                console.warn('[HubSpot] No se pudo cargar la configuración del chat.', error)
                return null
            })
            .finally(() => {
                this.loadingConfig = null
            })

        return this.loadingConfig
    },

    /**
     * Inyecta el script oficial de HubSpot una sola vez.
     * Evita duplicados aunque el componente se re-monte o se navegue.
     */
    injectScript(portalId) {
        if (!portalId) {
            return
        }
        if (this.scriptInjected || document.getElementById(HS_SCRIPT_ID)) {
            this.scriptInjected = true
            return
        }

        const script = document.createElement('script')
        script.id = HS_SCRIPT_ID
        script.type = 'text/javascript'
        script.async = true
        script.defer = true
        script.src = `https://js-na1.hs-scripts.com/${portalId}.js`
        document.head.appendChild(script)

        this.scriptInjected = true
    },

    /**
     * Ejecuta un callback cuando el widget de HubSpot está listo.
     * Usa la cola `hsConversationsOnReady` para soportar la carga diferida.
     */
    onReady(callback) {
        if (window.HubSpotConversations) {
            callback()
            return
        }
        window.hsConversationsOnReady = window.hsConversationsOnReady || []
        window.hsConversationsOnReady.push(callback)
    },

    /**
     * Obtiene el visitor identification token y configura HubSpot.
     * El header Authorization: Bearer lo inyecta axios automáticamente.
     */
    identify() {
        return axios.post('/hubspot/identify')
            .then(({ data }) => {
                const { token, email } = data || {}
                if (!token || !email) {
                    return
                }

                // Debe configurarse ANTES de que el widget arranque.
                window.hsConversationsSettings = {
                    identificationEmail: email,
                    identificationToken: token,
                }

                // Si el widget ya estaba cargado (p.ej. tras login en caliente),
                // hay que recargarlo para que tome la identificación.
                if (this.scriptInjected) {
                    this.refresh()
                }
            })
            .catch((error) => {
                console.warn('[HubSpot] No se pudo identificar al visitante.', error)
            })
    },

    /**
     * Recarga el widget identificado (limpia la sesión previa y vuelve a cargar).
     */
    refresh() {
        this.onReady(() => {
            try {
                window.HubSpotConversations.clear({ resetWidget: true })
                window.HubSpotConversations.widget.load()
            } catch (error) {
                console.warn('[HubSpot] No se pudo recargar el widget.', error)
            }
        })
    },

    /**
     * Limpia la identificación y resetea el chat (logout).
     * Evita que quede la sesión del usuario anterior.
     */
    reset() {
        window.hsConversationsSettings = {}
        if (window.HubSpotConversations) {
            try {
                window.HubSpotConversations.clear({ resetWidget: true })
            } catch (error) {
                console.warn('[HubSpot] No se pudo resetear el widget.', error)
            }
        }
    },

    /**
     * Orquesta la inicialización del chat según el estado de autenticación.
     *  - Invitado: carga el widget sin identificación.
     *  - Autenticado: identifica ANTES de inyectar el script.
     */
    async init(isAuthenticated) {
        const config = await this.loadConfig()
        if (!config || !config.portalId) {
            return
        }

        if (isAuthenticated) {
            // Configurar la identificación antes de cargar el script,
            // así el widget arranca ya identificado en la primera carga.
            await this.identify()
        }

        this.injectScript(config.portalId)
    },
}

export default service